import { isReference, isResource, normalizeOperationOutcome } from '@medplum/core';
import type { OperationOutcome, Reference, Resource, ResourceType } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

export type UseResourceOptions<T extends Resource, TMapped = T> = {
  /** Optional callback for error handling (legacy compatibility) */
  onError?: (outcome: OperationOutcome) => void;
  /** Optional mapping function to transform the result */
  select?: (data: T | undefined) => TMapped;
  /** Additional TanStack Query options */
  queryOptions?: Omit<
    UseQueryOptions<T | undefined, OperationOutcome, TMapped, readonly ['medplum', ResourceType, 'resource', string]>,
    'queryKey' | 'queryFn' | 'select'
  >;
};

/**
 * React Hook to use a FHIR resource with TanStack Query.
 * Handles the complexity of resolving references and caching resources.
 *
 * @param value - The resource, reference to resource, or undefined.
 * @param options - Optional configuration including error callback and query options.
 * @returns TanStack Query result with the resolved resource.
 *
 * @example
 * ```tsx
 * // With a reference
 * const { data: patient, isLoading } = useResource(patientRef);
 *
 * // With a full resource (no fetch needed)
 * const { data: patient } = useResource(existingPatient);
 *
 * // With error handling
 * const { data: patient, error } = useResource(patientRef, {
 *   onError: (outcome) => console.error(outcome)
 * });
 *
 * // With mapping
 * const { data: patientName } = useResource(patientRef, {
 *   select: (patient) => patient?.name?.[0]?.text
 * });
 * ```
 */
export function useResource<T extends Resource, TMapped = T>(
  value: Reference<T> | Partial<T> | undefined,
  options?: UseResourceOptions<T, TMapped>,
): UseQueryResult<TMapped, OperationOutcome> {
  const medplum = useMedplum();

  // Determine what we have: a full resource, a reference, or nothing
  const { hasFullResource, reference, fullResource } = useMemo(() => {
    if (!value) {
      return { hasFullResource: false, reference: undefined, fullResource: undefined };
    }

    if (isResource(value)) {
      return { hasFullResource: true, reference: undefined, fullResource: value as T };
    }

    if (isReference(value)) {
      return { hasFullResource: false, reference: value as Reference<T>, fullResource: undefined };
    }

    return { hasFullResource: false, reference: undefined, fullResource: undefined };
  }, [value]);

  // Create query key from the reference
  const queryKey = useMemo(() => {
    if (reference?.reference) {
      // Parse reference to extract resourceType and id
      // Format: "Patient/123" or "Patient/123/_history/1"
      const parts = reference.reference.split('/');
      const resourceType = parts[0] as ResourceType;
      const id = parts[1];
      return ['medplum', resourceType, 'resource', id] as const;
    }
    // For full resources or undefined, use a stable key that won't cause refetches
    // Use a non-existent resource type to avoid conflicts
    return ['medplum', '__local__' as ResourceType, 'resource', '__local__'] as const;
  }, [reference]);

  const queryResult = useQuery<T | undefined, OperationOutcome, TMapped, readonly ['medplum', ResourceType, 'resource', string]>({
    queryKey,
    queryFn: async () => {
      // This should only be called when we have a reference to fetch
      if (!reference) {
        return fullResource;
      }

      try {
        return await medplum.readReference(reference);
      } catch (error) {
        throw normalizeOperationOutcome(error);
      }
    },
    // Only fetch if we have a reference and don't have the full resource
    enabled: !hasFullResource && !!reference?.reference,
    // If we have a full resource, use it as the query data
    initialData: hasFullResource ? fullResource : undefined,
    // If we have a full resource, keep it fresh indefinitely (no background refetch)
    staleTime: hasFullResource ? Infinity : undefined,
    select: options?.select as any, // eslint-disable-line @typescript-eslint/no-explicit-any
    ...options?.queryOptions,
  });

  // Call the legacy onError callback if provided
  useEffect(() => {
    if (options?.onError && queryResult.error) {
      options.onError(queryResult.error);
    }
  }, [options, queryResult.error]);

  return queryResult;
}

/**
 * Query key factory for resource queries.
 *
 * Query keys now follow the hierarchical structure: ['medplum', resourceType, 'resource', id]
 * This allows invalidating all queries for a resource type (including searches and resources).
 */
export const resourceQueryKeys = {
  /**
   * Key for all resource queries of a specific type
   * @param resourceType - The FHIR resource type
   *
   * @example
   * ```ts
   * // Invalidate all Patient resource queries
   * queryClient.invalidateQueries({ queryKey: resourceQueryKeys.all('Patient') });
   * ```
   */
  all: (resourceType: ResourceType) => ['medplum', resourceType, 'resource'] as const,

  /**
   * Key for a specific resource by reference string
   * @param reference - The reference string (e.g., "Patient/123")
   *
   * @example
   * ```ts
   * // Invalidate a specific patient
   * queryClient.invalidateQueries({ queryKey: resourceQueryKeys.byReference('Patient/123') });
   * ```
   */
  byReference: (reference: string) => {
    const parts = reference.split('/');
    const resourceType = parts[0] as ResourceType;
    const id = parts[1];
    return ['medplum', resourceType, 'resource', id] as const;
  },

  /**
   * Key for a specific resource by Reference object
   * @param ref - The Reference object
   *
   * @example
   * ```ts
   * // Invalidate a specific patient by reference object
   * queryClient.invalidateQueries({ queryKey: resourceQueryKeys.byReferenceObject(patientRef) });
   * ```
   */
  byReferenceObject: <T extends Resource>(reference: Reference<T>) => {
    if (!reference.reference) {
      return ['medplum', '__unknown__' as ResourceType, 'resource', '__unknown__'] as const;
    }
    const parts = reference.reference.split('/');
    const resourceType = parts[0] as ResourceType;
    const id = parts[1];
    return ['medplum', resourceType, 'resource', id] as const;
  },

  /**
   * Key for a specific resource by type and ID
   * @param resourceType - The FHIR resource type
   * @param id - The resource ID
   *
   * @example
   * ```ts
   * // Invalidate a specific patient by type and ID
   * queryClient.invalidateQueries({ queryKey: resourceQueryKeys.byId('Patient', '123') });
   * ```
   */
  byId: (resourceType: ResourceType, id: string) => ['medplum', resourceType, 'resource', id] as const,
} as const;
